/**
 * Actions Service
 * T039: Implement ActionsService for action operations
 */

import type { GatewayClient } from './client'
import type {
  ActionDetail,
  ActionListResponse,
  ListActionsOptions,
  CostCalculationResponse,
  ActionExecuteResponse,
  ActionJobResponse,
} from './types'

// ============================================================================
// Types
// ============================================================================

export interface ActionsService {
  /**
   * List available actions
   */
  list(options?: ListActionsOptions): Promise<ActionListResponse>

  /**
   * Get action details
   */
  get(actionName: string): Promise<ActionDetail>

  /**
   * Calculate cost for an action
   */
  calculateCost(
    actionName: string,
    request: unknown
  ): Promise<CostCalculationResponse>

  /**
   * Execute action synchronously
   */
  execute(actionName: string, request: unknown): Promise<ActionExecuteResponse>

  /**
   * Submit async job.
   * The request should match the action's inputSchema.
   */
  submitJob(actionName: string, request: unknown): Promise<ActionJobResponse>

  /**
   * Get job status
   */
  getJobStatus(actionName: string, jobId: string): Promise<ActionJobResponse>
}

// ============================================================================
// Implementation
// ============================================================================

/**
 * Create an ActionsService instance
 */
export function createActionsService(client: GatewayClient): ActionsService {
  return {
    async list(options?: ListActionsOptions): Promise<ActionListResponse> {
      return client.get<ActionListResponse>('/v1/actions', {
        params: options as Record<
          string,
          string | number | boolean | undefined
        >,
      })
    },

    async get(actionName: string): Promise<ActionDetail> {
      return client.get<ActionDetail>(`/v1/actions/${actionName}`)
    },

    async calculateCost(
      actionName: string,
      request: unknown
    ): Promise<CostCalculationResponse> {
      return client.post<CostCalculationResponse>(
        `/v1/actions/${actionName}/cost`,
        request
      )
    },

    async execute(
      actionName: string,
      request: unknown
    ): Promise<ActionExecuteResponse> {
      return client.post<ActionExecuteResponse>(
        `/v1/actions/${actionName}/execute`,
        request
      )
    },

    async submitJob(
      actionName: string,
      request: unknown
    ): Promise<ActionJobResponse> {
      return client.post<ActionJobResponse>(
        `/v1/actions/${actionName}/jobs`,
        request
      )
    },

    async getJobStatus(
      actionName: string,
      jobId: string
    ): Promise<ActionJobResponse> {
      return client.get<ActionJobResponse>(
        `/v1/actions/${actionName}/jobs/${jobId}`
      )
    },
  }
}
